import { ERP_DOMAIN, LOGIN_PATH } from '@/constants';
import { find, isEmpty } from 'lodash';
import queryString from 'query-string';

import Cookies from '@/utils/cookie';
import { getScmParams } from '@/utils/erpHelper';

/**
 * 获取 epaas 路径
 * @param path
 * @param params
 * @returns
 */
const getEPaaSPath = (
  path: string = '',
  params: { [key: string]: string | boolean | undefined } = {},
) => {
  const appendString = path?.indexOf('?') > 0 ? '&' : '?';
  let _finalPath = path;
  Object.keys(params || {})?.forEach?.((key) => {
    // 如果有 :id 这样的动态参数，需要替换
    let reg = new RegExp(`/:${key}`, 'ig');
    if (reg.test(path)) {
      _finalPath = path.replaceAll(reg, `/${params[key] || ''}`);
    }
  });

  // 这里需要对重复参数做下去重, path 不一定是个完整的 url 协议, 这里不推荐用 parseUrl 来解
  try {
    const [_baseUrl, _search, _hash] = _finalPath
      .match(/^([^?#]*)\??([^#]*)#?(.*)$/)
      .slice(1);

    let __finalPath =
      _baseUrl +
      '?' +
      queryString.stringify(
        {
          ...queryString.parse(_search),
          ...params,
        },
        { sort: false },
      );

    if (_hash) {
      __finalPath += `#${_hash}`;
    }

    return __finalPath;
  } catch (error) {}

  return (
    _finalPath +
    appendString +
    queryString.stringify(
      {
        ...params,
      },
      { sort: false },
    )
  );
};

/**
 * 获取 erp 路径
 * @param url
 * @returns
 */
const getErpPath = (url = ''): string => {
  if (isEmpty(url)) {
    return '';
  }
  let _url = url.trim();
  if (_url.startsWith('http')) {
    return _url.replace('http://', 'https://');
  } else if (_url.startsWith('//')) {
    return 'https:' + _url;
  } else if (_url.startsWith('/')) {
    return `${ERP_DOMAIN}${_url}`;
  } else if (_url.startsWith('epaas://')) {
    return _url;
  } else {
    return `${ERP_DOMAIN}/${_url}`;
  }
};

const getSrcPath = (url = ''): string => {
  let srcPrefix = {
    loc: 'https://dev-src.jushuitan.com',
    dev: 'https://dev-src.jushuitan.com',
    test: 'https://test-src.jushuitan.com',
    pre: 'https://pre-src.erp321.com',
    prod: 'https://src.erp321.com',
  }[UMI_ENV || 'prod'];

  if (isEmpty(url)) {
    return '';
  }
  const _url = url.trim();
  if (_url.startsWith('http')) {
    return _url.replace('http://', 'https://');
  } else if (_url.startsWith('//')) {
    return 'https:' + _url;
  } else if (_url.startsWith('/')) {
    return `${srcPrefix}${_url}`;
  } else if (_url.startsWith('epaas://')) {
    return _url;
  } else {
    return `${srcPrefix}/${_url}`;
  }
};

/**
 * 链接添加容器标识(_c)
 * @param url
 * @returns
 */
const addContainer = (url: string, params: any): string => {
  const urlObj: queryString.ParsedUrl = queryString.parseUrl(url, {
    sort: false,
  });

  const scmParams = getScmParams(urlObj.url);

  let resutUrl = queryString.stringifyUrl(
    {
      url: urlObj.url,
      query: {
        ...urlObj.query,
        _c: 'jst-epaas',
        ...scmParams,
        ...(params || {}),
      },
    },
    { sort: false },
  );

  try {
    const hash = url.split('#')[1];
    if (hash) {
      resutUrl += `#${hash}`;
    }
  } catch (error) {}

  return resutUrl;
};

/**
 * 从 epaasUrl 获取 serviceKey
 * @param epaasUrl
 * @returns
 */
function getServiceKeyFromEpaasUrl(epaasUrl?: string) {
  return epaasUrl?.replace('epaas://', '');
}

function getEntranceByPageId(
  service: Definitions.ServiceInfo | undefined,
  pageId: string,
): Definitions.ServiceEntrance {
  if (!service) {
    return {};
  }
  let entrance;
  if (isEmpty(pageId)) {
    entrance = find(service.entrances, { type: 1 });

    if (isEmpty(entrance)) {
      entrance = (service.entrances || [])?.[0];
    }
  } else {
    entrance = find(service.entrances, { pageId });
  }

  return entrance || {};
}

// 退出登录
function logout() {
  // 获取顶级域名
  let cookieDomain = '.' + (ERP_DOMAIN.split('.').slice(-2) || []).join('.');

  Cookies.remove('u_id', { domain: cookieDomain });
  Cookies.remove('u_co_name', { domain: cookieDomain });
  Cookies.remove('u_co_id', { domain: cookieDomain });
  Cookies.remove('u_name', { domain: cookieDomain });
  Cookies.remove('u_lid', { domain: cookieDomain });

  Cookies.remove('u_json', { domain: cookieDomain });

  Cookies.remove('u_env', { domain: cookieDomain });
  Cookies.remove('u_env_next', { domain: cookieDomain });
  Cookies.remove('u_env_fp', { domain: cookieDomain });

  // 三方仓用户,退出到三方仓登录页
  if (Cookies.get('u_isTPWS') === '1') {
    const tpwsLoginPage: string =
      {
        dev: 'https://dev-wms.jushuitan.com/login.aspx',
        test: 'https://test-wms.jushuitan.com/login.aspx',
        pre: 'https://pre-wms.erp321.com/login.aspx',
        prod: 'https://wms.erp321.com/login.aspx',
      }[UMI_ENV || 'prod'] || LOGIN_PATH;
    location.href = tpwsLoginPage;
  } else if (
    Cookies.get('u_isTPWS') === '0' &&
    (location.origin.indexOf('wms.erp321.com') > -1 ||
      location.origin.indexOf('wms.jushuitan.com') > -1)
  ) {
    const normalLoginPage: string =
      {
        dev: 'https://dev-scm.jushuitan.com/login.aspx',
        test: 'https://test-oms.jushuitan.com/login.aspx',
        pre: 'https://pre-1w.erp321.com/login.aspx',
        prod: 'https://www.erp321.com/login.aspx',
      }[UMI_ENV || 'prod'] || LOGIN_PATH;
    location.href = normalLoginPage;
  } else {
    location.href = LOGIN_PATH;
  }
}

// 根据域名判断是否代理环境
function isProxy(): boolean {
  try {
    const { host } = window.location;

    const ins = [
      'do.jushuitan.com',
      'dopre.jushuitan.com',
      'pf1.jushuitan.com',
      'pf.jushuitan.com',
    ];
    const reg1 = /^(daili(-?)([a-z0-9-]*))\.jushuitan\.com/i;
    const reg2 = /^(pre-([a-z0-9]*)-daili)\.jushuitan\.com/i;

    return ins.includes(host) || reg1.test(host) || reg2.test(host);
  } catch (error) {}
  return false;
}

export {
  getEPaaSPath,
  getErpPath,
  getSrcPath,
  getServiceKeyFromEpaasUrl,
  getEntranceByPageId,
  logout,
  addContainer,
  isProxy,
};
